import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface Fudge {
  id: string;
  tagId: string | null;
  description: string;
  changeDetail: string;
  location: string | null;
  kks: string | null;
  appliedByName: string;
  appliedAt: string;
  reinstatedByName: string | null;
  reinstatedAt: string | null;
  reinstatedManually: number;
  reappliedFromId: string | null;
  status: string;
}

export default function FudgeHistoryPage() {
  const [fudges, setFudges] = useState<Fudge[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchHistory = useCallback(async () => {
    const r = await fetch("/api/fudges/history");
    if (r.ok) setFudges(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  function fmt(iso: string) {
    return new Date(iso).toLocaleDateString();
  }

  async function handleReapply(f: Fudge) {
    const r = await fetch("/api/fudges/reapply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceId: f.id }),
    });
    if (!r.ok) return;
    const prefill = await r.json();
    localStorage.setItem("fudge_prefill", JSON.stringify({
      ...prefill,
      expiresAt: Date.now() + 30 * 60 * 1000,
    }));
    navigate("/fudges/reapply");
  }

  const filtered = fudges.filter((f) => {
    const q = search.toLowerCase();
    return (
      f.description.toLowerCase().includes(q) ||
      f.changeDetail.toLowerCase().includes(q) ||
      (f.location ?? "").toLowerCase().includes(q) ||
      f.appliedByName.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Fudge History</h1>
          <p className="text-sm text-slate-500 mt-0.5">All fudges — active and reinstated</p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-56"
        />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">{search ? "No results." : "No fudges recorded yet."}</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3">Change Detail</th>
                    <th className="text-left px-4 py-3">Location</th>
                    <th className="text-left px-4 py-3">KKS</th>
                    <th className="text-left px-4 py-3">Applied By</th>
                    <th className="text-left px-4 py-3">Applied</th>
                    <th className="text-left px-4 py-3">Reinstated By</th>
                    <th className="text-left px-4 py-3">Reinstated</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f) => (
                    <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900 max-w-xs">
                        {f.description}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs">{f.changeDetail}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{f.location ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono">{f.kks ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{f.appliedByName}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{fmt(f.appliedAt)}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{f.reinstatedByName ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{f.reinstatedAt ? fmt(f.reinstatedAt) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                          f.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-500"
                        }`}>
                          {f.status === "active" ? "Active" : "Reinstated"}
                        </span>
                        {f.reinstatedManually === 1 && (
                          <span className="ml-1 text-xs text-amber-600">(manual)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {f.status === "reinstated" && (
                          <button onClick={() => handleReapply(f)}
                            className="text-xs text-blue-600 hover:underline">
                            Re-apply
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — swipe left to reveal Re-apply */}
            <div className="md:hidden px-4 py-2 text-xs text-slate-400 border-b border-slate-100">
              Swipe left on a reinstated fudge to re-apply it
            </div>
            <div className="md:hidden divide-y divide-slate-100">
              {filtered.map((f) => (
                <SwipeCard
                  key={f.id}
                  onAction={f.status === "reinstated" ? () => handleReapply(f) : undefined}
                  actionLabel="Re-apply"
                >
                  <div className="px-4 py-4 space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 flex-1">{f.description}</p>
                      <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                        f.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {f.status === "active" ? "Active" : "Reinstated"}
                        {f.reinstatedManually === 1 && " (manual)"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Applied by {f.appliedByName} · {fmt(f.appliedAt)}
                    </p>
                    {f.kks && (
                      <p className="text-xs font-mono text-slate-500">{f.kks}</p>
                    )}
                    {f.status === "reinstated" && f.reinstatedByName && (
                      <p className="text-xs text-slate-400">
                        Reinstated by {f.reinstatedByName} · {f.reinstatedAt ? fmt(f.reinstatedAt) : ""}
                        {f.reinstatedManually === 1 && " (manual)"}
                      </p>
                    )}
                  </div>
                </SwipeCard>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const ACTION_WIDTH = 88;

function SwipeCard({
  children,
  onAction,
  actionLabel,
}: {
  children: React.ReactNode;
  onAction?: () => void;
  actionLabel: string;
}) {
  const [offset, setOffset] = useState(0);
  const startX = useRef(0);
  const isDragging = useRef(false);

  function handleTouchStart(e: React.TouchEvent) {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!isDragging.current || !onAction) return;
    const dx = e.touches[0].clientX - startX.current;
    if (dx < 0) setOffset(Math.max(dx, -ACTION_WIDTH));
    else if (offset < 0) setOffset(Math.min(dx + offset, 0));
  }

  function handleTouchEnd() {
    isDragging.current = false;
    setOffset((prev) => (prev < -ACTION_WIDTH / 2 ? -ACTION_WIDTH : 0));
  }

  return (
    <div className="relative overflow-hidden">
      {/* Action button revealed behind the card */}
      {onAction && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-center bg-blue-600"
          style={{ width: ACTION_WIDTH }}
        >
          <button
            onClick={() => { setOffset(0); onAction(); }}
            className="text-white text-xs font-semibold px-3 text-center"
          >
            {actionLabel}
          </button>
        </div>
      )}

      {/* Card content — slides left on swipe */}
      <div
        style={{
          transform: `translateX(${offset}px)`,
          transition: isDragging.current ? "none" : "transform 0.2s ease",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => { if (offset !== 0) setOffset(0); }}
        className="bg-white relative"
      >
        {children}
      </div>
    </div>
  );
}
